import React from 'react';
import { createRendererToString } from '@wix/cli-ui-kit';
import { isCliError } from '@wix/cli-error';
import type { ReactElement } from 'react';
import type { LogErrorOptions, LogOptions, Logger as ViteLogger } from 'vite';
import { createLogger as createViteLogger } from 'vite';
import { ErrorViewer } from '@wix/cli-error-reporting';

function isInfoMessageFiltered(msg: string) {
  return [/Port \d{1,5} is in use, trying another one/].some((x) =>
    x.test(msg)
  );
}

export interface Logger extends ViteLogger {
  info: (msg: string | ReactElement, options?: LogOptions) => void;
  warn: (msg: string | ReactElement, options?: LogOptions) => void;
  warnOnce: (msg: string | ReactElement, options?: LogOptions) => void;
  error: (msg: string | ReactElement, options?: LogErrorOptions) => void;
}

export function createLogger({
  timestamp,
}: { timestamp?: boolean } = {}): Logger {
  const renderer = createRendererToString({ stripAnsi: false });

  const originalLogger = createViteLogger('info', {
    allowClearScreen: false,
    prefix: '',
  });

  const defaultOptions = { timestamp };

  const logMessage = (
    level: 'info' | 'warn' | 'warnOnce' | 'error',
    msg: string | ReactElement,
    options?: LogOptions | LogErrorOptions
  ) => {
    const logOptions = { ...options, ...defaultOptions };

    if (typeof msg === 'string') {
      if (!isInfoMessageFiltered(msg)) {
        return originalLogger[level](msg, logOptions);
      }

      return null;
    }

    // https://github.com/wix-private/wix-cli/pull/2745/files#r1826977936
    return setTimeout(() =>
      originalLogger[level](renderer.render(msg), logOptions)
    );
  };

  return {
    ...originalLogger,
    info: (msg, options?) => logMessage('info', msg, options),
    error: (msg, options?) => {
      // vite has an issue with types and `msg` can in some cases be an Error
      if (msg instanceof Error) {
        return logMessage('error', <ErrorViewer error={msg} />);
      }
      if (isCliError(options?.error)) {
        return logMessage('error', <ErrorViewer error={options.error} />);
      }

      return logMessage('error', msg, options);
    },
    warn: (msg, options?) => logMessage('warn', msg, options),
    warnOnce: (msg, options?) => logMessage('warnOnce', msg, options),
  } satisfies Logger;
}
